import classNames from 'classnames';
import React, {ChangeEventHandler, FocusEventHandler, memo, MouseEventHandler, useCallback, useState} from 'react';
import style from './style.module.scss';
import {TClosureCallback} from 'core/types';
import Icon from 'components/Icon';
import {MAX_COUNTER_VALUE} from 'core/constants';

export const COUNTER_SIZE = {
    LARGE: 'large',
    LARGE_MEDIUM: 'large-medium',
    MEDIUM: 'medium',
} as const;

export const COUNTER_BACKGROUND: Record<string, TCounterBackground> = {
    BLUE: 'blue',
    GRAY: 'gray',
    GRAY_LIGHT: 'gray-light',
    TRANSPARENT: 'transparent',
};

export const COUNTER_ACTION: Record<string, TCounterAction> = {
    ADD: 'add',
    REMOVE: 'remove',
};

export type TCounterSize = (typeof COUNTER_SIZE)[keyof typeof COUNTER_SIZE];
export type TCounterAction = 'add' | 'remove';
export type TCounterBackground = 'gray-light' | 'gray' | 'blue' | 'transparent';

export interface IProps {
    className?: string;
    size?: TCounterSize;
    background?: TCounterBackground;
    handleAddClick?: MouseEventHandler<HTMLButtonElement>;
    handleRemoveClick?: MouseEventHandler<HTMLButtonElement>;
    handleChange?: ChangeEventHandler<HTMLInputElement>;
    handleInputBlur?: FocusEventHandler<HTMLInputElement>;
    count: number | null;
    isLoading?: boolean;
    minCount?: number;
    maxCount?: number;
    isHorizontal?: boolean;
    isStaticWidth?: boolean;
    isCountInMeasures?: boolean;
}

export const Counter = memo<IProps>(function Counter({
    className,
    size = COUNTER_SIZE.LARGE,
    background = COUNTER_BACKGROUND.TRANSPARENT,
    handleAddClick,
    handleRemoveClick,
    count,
    handleChange,
    handleInputBlur,
    isLoading = false,
    minCount = 0,
    maxCount = MAX_COUNTER_VALUE,
    isHorizontal = false,
    isStaticWidth = false,
    isCountInMeasures = true,
}) {
    const [isShowUnit, setIsShowUnit] = useState(true);

    const onClick = useCallback<TClosureCallback<TCounterAction, MouseEventHandler<HTMLButtonElement>>>(
        (action) => {
            return (e) => {
                if (isLoading) {
                    return;
                }

                if (action === COUNTER_ACTION.REMOVE && handleRemoveClick) {
                    handleRemoveClick(e);
                }

                if (action === COUNTER_ACTION.ADD && handleAddClick) {
                    handleAddClick(e);
                }
            };
        },
        [handleAddClick, handleRemoveClick, isLoading],
    );

    const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (e) => {
            const inputValue = Number(e.target.value);

            if (inputValue < minCount) {
                return;
            }

            handleChange?.(e);
        },
        [handleChange, minCount],
    );

    const onFocus = useCallback(() => {
        setIsShowUnit(false);
    }, []);

    const onBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
        (e) => {
            setIsShowUnit(true);
            handleInputBlur?.(e);
        },
        [handleInputBlur],
    );

    return (
        <div
            className={classNames(style.counter, 'counter', className, {
                [style[size]]: Boolean(size),
                [style.horizontal]: isHorizontal,
                [style.static]: isStaticWidth,
            })}
        >
            <button
                className={classNames(style.button, style.decrement, 'counter-button', {
                    [style[background]]: Boolean(background),
                })}
                data-prevent-default-marker={true}
                data-stop-propagation-marker={true}
                disabled={minCount >= Number(count)}
                onClick={onClick(COUNTER_ACTION.REMOVE)}
                type={'button'}
            >
                <Icon className={style.minus} icon={'minus'} />
            </button>
            <input
                className={classNames(style.input, 'counter-input', {
                    [style[background]]: Boolean(background),
                })}
                data-prevent-default-marker={true}
                data-stop-propagation-marker={true}
                max={maxCount}
                onBlur={onBlur}
                onChange={onChange}
                onFocus={onFocus}
                type={'text'}
                value={`${0 === count ? '' : count}${isShowUnit && isCountInMeasures ? ' шт.' : ''}`}
            />
            <button
                className={classNames(style.button, style.increment, 'counter-button', {
                    [style[background]]: Boolean(background),
                })}
                data-prevent-default-marker={true}
                data-stop-propagation-marker={true}
                disabled={maxCount <= Number(count)}
                onClick={onClick(COUNTER_ACTION.ADD)}
                type={'button'}
            >
                <Icon className={style.plus} icon={'plus'} />
            </button>
        </div>
    );
});
